import { useState } from 'react';

// ---------------------------------------------------------------------------
// Status icons — same SVGs as ExtractionPipelineTimeline at w-3.5 h-3.5
// ---------------------------------------------------------------------------
function CheckIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg className="w-3.5 h-3.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Pick the right icon for a step's status
// ---------------------------------------------------------------------------
function StatusIcon({ status }) {
  if (status === 'complete') return <CheckIcon />;
  if (status === 'warning') return <WarningIcon />;
  if (status === 'error') return <ErrorIcon />;
  return <div className="w-2 h-2 rounded-full bg-gray-300" />;
}

// ---------------------------------------------------------------------------
// Single validation row — expandable if step.warnings exists
// ---------------------------------------------------------------------------
function ValidationRow({ step }) {
  const hasWarnings = Array.isArray(step.warnings) && step.warnings.length > 0;
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-2 px-1 rounded ${hasWarnings ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={hasWarnings ? () => setExpanded(!expanded) : undefined}
      >
        {/* Status icon */}
        <span className="flex-shrink-0">
          <StatusIcon status={step.status} />
        </span>

        {/* Label */}
        <span className="text-sm font-medium text-gray-900 truncate">{step.label}</span>

        {/* Expand chevron for rows with warnings */}
        {hasWarnings && (
          <svg
            className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform duration-150 ${expanded ? '' : '-rotate-90'}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        )}

        {/* Duration pill on the right */}
        {step.duration && (
          <span className="ml-auto px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono flex-shrink-0" style={{ fontSize: '10px' }}>
            {step.duration}
          </span>
        )}
      </div>

      {/* Detail text (always visible) */}
      {step.detail && (
        <p className="text-xs text-gray-400 pl-6 -mt-1 mb-1">{step.detail}</p>
      )}

      {/* Expandable warnings list */}
      {hasWarnings && expanded && (
        <ul className="pl-6 pb-1 space-y-0.5">
          {step.warnings.map((w, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-amber-700">
              <span className="flex-shrink-0 mt-0.5">
                <WarningIcon />
              </span>
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ValidationResults({ steps = [] }) {
  if (!steps || steps.length === 0) return null;

  return (
    <div className="divide-y divide-gray-100">
      {steps.map((step, i) => (
        <ValidationRow key={step.stepId || i} step={step} />
      ))}
    </div>
  );
}
